import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/custom/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/custom/progress";
import { useTimeSettings, TimeSettings } from "@/state/time-settings";
import { TzPicker } from "@/routes/dashboard/-tz-picker";
import { FormDatePickerItem } from "@/components/custom/form-inputs/date-picker";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormSelectInputItem } from "@/components/custom/form-inputs/select-input/select-input";
import { OptionType } from "@/components/select-input/select-input";
import { useOptionsMapping } from "@/components/custom/form-inputs/select-input/common";
import {
  MultiStepDialogContent,
  DialogPage,
} from "@/components/custom/paginated-dialog-content/multi-step-dialog-content";
import {
  RunImmediatelyInput,
  ScheduleSpecificInput,
} from "@/components/custom/scheduled-deployments/run-runImmediately-input";
import { client } from "@/client";
import { FormCheckbox } from "@/components/custom/form-inputs/checkbox";
import { Input } from "@/components/ui/input";
import type { ScheduleJobScheduleDto } from "@/client/__generated__/types.gen";

type UploadCatalogResult = {
  updatedLabels?: number;
  scheduledJobs?: number;
  immediateUploads?: number;
};

const uploadCatalogSchema = z
  .object({
    uploadMode: z.string().min(1),
    labelUploadMode: z.enum(["full", "partial"]).optional(),
    runImmediately: z.boolean(),
    schedule: z.date().optional(),
    timezone: z
      .string()
      .min(1, { message: "Please select a timezone" })
      .optional(),
    file: z.custom<File>((v) => v instanceof File, {
      message: "File is required",
    }),
    siteId: z.number().optional(),
    groupId: z.number().optional(),
    immediateApply: z.boolean(),
    jobName: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      return data.schedule !== undefined;
    },
    {
      message: "Please choose a schedule time",
      path: ["schedule"],
    },
  )
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      return data.timezone && data.timezone.length > 0;
    },
    {
      message: "Please select a timezone",
      path: ["timezone"],
    },
  )
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      if (!data.schedule) {
        return true;
      }
      return data.schedule > new Date();
    },
    {
      message:
        'Cannot schedule in the past. Use "Run Immediately" to upload now.',
      path: ["schedule"],
    },
  );

export const UploadCatalogDialog = ({
  onClose,
  initialGroupId,
  initialSiteId,
  initialGroupIds,
  initialSiteIds,
}: {
  onClose?: () => void;
  initialGroupId?: number;
  initialSiteId?: number;
  initialGroupIds?: number[];
  initialSiteIds?: number[];
}) => {
  const { t } = useTranslation();
  const hasInitialGroups =
    initialGroupId !== undefined || (initialGroupIds?.length ?? 0) > 0;
  const hasInitialSites =
    initialSiteId !== undefined || (initialSiteIds?.length ?? 0) > 0;
  const timeSettings = useTimeSettings();
  const [currentStep, setCurrentStep] = useState(
    hasInitialGroups || hasInitialSites ? 1 : 0,
  );
  const queryClient = useQueryClient();
  const [uploadResults, setUploadResults] =
    useState<UploadCatalogResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
    currentGroupName?: string;
  } | null>(null);

  // Animated progress that increases steadily
  const [animatedProgress, setAnimatedProgress] = useState(0);

  // Animate progress at 1% per second when uploading
  useEffect(() => {
    if (!uploadProgress) {
      setAnimatedProgress(0);
      return;
    }

    // Calculate actual progress
    const actualProgress =
      (uploadProgress.current / uploadProgress.total) * 100;

    // If upload is complete, jump to 100%
    if (actualProgress >= 100) {
      setAnimatedProgress(100);
      return;
    }

    const interval = setInterval(() => {
      setAnimatedProgress((prev) => {
        // Always increase by 1% per second, but cap at 90%
        if (prev >= 90) {
          return 90; // Stay at 90% until upload completes
        }
        return prev + 1;
      });
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [uploadProgress]);

  const form = useForm<z.infer<typeof uploadCatalogSchema>>({
    resolver: zodResolver(uploadCatalogSchema),
    defaultValues: {
      uploadMode: hasInitialGroups
        ? "group"
        : hasInitialSites
          ? "site"
          : "master",
      labelUploadMode: "full",
      runImmediately: true,
      schedule: new Date(),
      timezone: timeSettings.timeZone,
      file: undefined,
      siteId: initialSiteId,
      groupId: initialGroupId,
      immediateApply: false,
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (data: {
      file: File;
      siteIds?: number[];
      groupIds?: number[];
      labelUploadMode?: "full" | "partial";
      schedule?: ScheduleJobScheduleDto;
      immediateApply?: boolean;
      name?: string;
    }) => {
      const mapUpdatedLabels = (result: unknown) => {
        const updatedLabels = (result as any)?.updatedLabels;
        return typeof updatedLabels === "number" ? updatedLabels : 0;
      };

      setUploadProgress({ current: 0.5, total: 1 });

      const result = await client.catalog.uploadCSV({
        formData: {
          file: data.file,
          siteIds: data.siteIds,
          groupIds: data.groupIds,
          uploadMode: data.labelUploadMode,
          schedule: data.schedule,
          immediateApply: data.immediateApply,
          name: data.name,
        },
      });

      setUploadProgress({ current: 1, total: 1 });

      const isScheduled = !!data.schedule;
      return {
        updatedLabels: mapUpdatedLabels(result),
        scheduledJobs: isScheduled ? 1 : 0,
        immediateUploads: isScheduled ? 0 : 1,
      } as UploadCatalogResult;
    },
    onSuccess: async (data) => {
      setUploadResults(data);
      setUploadError(null);
      // Wait 500ms to show 100% progress before clearing
      await new Promise((resolve) => setTimeout(resolve, 500));
      setUploadProgress(null);
      queryClient.invalidateQueries({
        queryKey: client.catalog.listSummaries.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: client.catalogUploadJobs.listJobs.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: client.catalogUploadJobs.listScheduled.queryKey,
      });
    },
    onError: (error: any) => {
      setUploadProgress(null);
      setUploadError(error?.body?.message || t("Upload failed"));
      // Don't show toast, we'll display the error in the dialog
    },
  });

  const uploadMode = form.watch("uploadMode");
  const file = form.watch("file");
  const siteId = form.watch("siteId");
  const groupId = form.watch("groupId");
  const runImmediately = form.watch("runImmediately");
  const timezone = form.watch("timezone");
  const jobName = form.watch("jobName");

  // Clear siteId when switching away from site mode
  useEffect(() => {
    if (uploadMode !== "site") {
      form.setValue("siteId", undefined);
    }
  }, [uploadMode, form]);

  // Clear groupId when switching away from group mode
  useEffect(() => {
    if (uploadMode !== "group") {
      form.setValue("groupId", undefined);
    }
  }, [uploadMode, form]);

  const { data: sitesData } = useQuery({
    ...client.sites.listMinimal.asQueryOptions({
      page: 1,
      limit: 1000,
    }),
    enabled: uploadMode === "site",
  });

  const { data: groupsData } = useQuery({
    ...client.siteGroups.listSummaries.asQueryOptions({
      page: 1,
      limit: 1000,
    }),
    enabled: uploadMode === "group",
  });

  // Create options for multi-select
  const siteOptions: OptionType<number>[] = useMemo(
    () =>
      sitesData?.results?.map((site: any) => ({
        value: site.id!,
        label: site.name!,
      })) || [],
    [sitesData],
  );

  const groupOptions: OptionType<number>[] = useMemo(
    () =>
      groupsData?.results?.map((group: any) => ({
        value: group.id!,
        label: group.name!,
      })) || [],
    [groupsData],
  );

  // Create mapping functions using the hook
  const siteMapping = useOptionsMapping(siteOptions);
  const groupMapping = useOptionsMapping(groupOptions);

  // Every selected target for the CURRENTLY selected mode — a manual pick via Page1's single-select
  // dropdown (siteId/groupId) always wins over the original bulk pre-fill (initialSiteIds/initialGroupIds),
  // since a live form value only gets set by the user actively choosing something different. Gating by
  // uploadMode also guarantees only one of these is ever non-empty, matching the backend's
  // "siteIds or groupIds, never both" contract even after switching modes via Back navigation.
  const targetSiteIds = useMemo(() => {
    if (uploadMode !== "site") return [];
    if (siteId !== undefined) return [siteId];
    return initialSiteIds?.length ? initialSiteIds : [];
  }, [uploadMode, initialSiteIds, siteId]);
  const targetGroupIds = useMemo(() => {
    if (uploadMode !== "group") return [];
    if (groupId !== undefined) return [groupId];
    return initialGroupIds?.length ? initialGroupIds : [];
  }, [uploadMode, initialGroupIds, groupId]);

  const computedJobName = useMemo(() => {
    const timing = runImmediately ? "Immediate" : "Scheduled";
    if (targetSiteIds.length > 0) {
      if (targetSiteIds.length === 1) {
        const name = siteMapping(targetSiteIds[0])?.label;
        const scope = name ? `Site '${name}'` : "Site";
        return `Catalog Update — ${scope} — ${timing}`;
      }
      return `Catalog Update — ${t("{{count}} Sites", { count: targetSiteIds.length })} — ${timing}`;
    }
    if (targetGroupIds.length > 0) {
      if (targetGroupIds.length === 1) {
        const name = groupMapping(targetGroupIds[0])?.label;
        const scope = name ? `Group '${name}'` : "Group";
        return `Catalog Update — ${scope} — ${timing}`;
      }
      return `Catalog Update — ${t("{{count}} Groups", { count: targetGroupIds.length })} — ${timing}`;
    }
    return `Catalog Update — Base — ${timing}`;
  }, [
    runImmediately,
    targetSiteIds,
    targetGroupIds,
    siteMapping,
    groupMapping,
    t,
  ]);

  const isJobNameCustomized = useRef(false);

  useEffect(() => {
    if (!isJobNameCustomized.current) {
      form.setValue("jobName", computedJobName);
    }
  }, [computedJobName, form]);

  const [validationState, setValidationState] = useState<
    "idle" | "validating" | "valid" | "invalid"
  >("idle");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationResults, setValidationResults] = useState<{
    isCsv: boolean;
    hasLabel: boolean;
    foundOptional: string[];
    missingOptional: string[];
    emptyLabelRows: number[];
    duplicateLabels: string[];
    duplicateTranslatedLabels: Array<{
      label: string;
      items: Array<{ plu: string; lineNumber: number }>;
    }>;
    extraFields: string[];
  }>({
    isCsv: false,
    hasLabel: false,
    foundOptional: [],
    missingOptional: [],
    emptyLabelRows: [],
    duplicateLabels: [],
    duplicateTranslatedLabels: [],
    extraFields: [],
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        form.setValue("file", acceptedFiles[0], { shouldValidate: true });
        setValidationState("idle");
        setValidationErrors([]);
        setValidationResults({
          isCsv: false,
          hasLabel: false,
          foundOptional: [],
          missingOptional: [],
          emptyLabelRows: [],
          duplicateLabels: [],
          duplicateTranslatedLabels: [],
          extraFields: [],
        });
      }
    },
    [form],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  const validateFile = async (fileToValidate: File) => {
    setValidationState("validating");
    setValidationErrors([]);
    setValidationResults({
      isCsv: false,
      hasLabel: false,
      foundOptional: [],
      missingOptional: [],
      emptyLabelRows: [],
      duplicateLabels: [],
      duplicateTranslatedLabels: [],
      extraFields: [],
    });

    // Basic file type check
    const isCsv =
      fileToValidate.type === "text/csv" ||
      fileToValidate.name.endsWith(".csv");

    if (!isCsv) {
      setValidationErrors([t("File must be a CSV file.")]);
      setValidationState("invalid");
      return false;
    }

    try {
      const text = await fileToValidate.text();
      const lines = text.split(/\r\n|\n/);
      if (
        lines.length === 0 ||
        (lines.length === 1 && lines[0].trim() === "")
      ) {
        setValidationErrors([t("File is empty.")]);
        setValidationState("invalid");
        return false;
      }

      const headerLine = lines[0];
      const headers = headerLine.split(",").map((h) => h.trim());

      const errors: string[] = [];
      const optionalFields = [
        "name",
        "englishName",
        "price",
        "family",
        "priceBy",
        "outOfScope",
        "translatedLabel",
      ];
      const foundOptional: string[] = [];
      const missingOptional: string[] = [];

      const labelIndex = headers.indexOf("label");
      const hasLabel = labelIndex !== -1;
      if (!hasLabel) {
        errors.push(t('Missing required column: "label"'));
      }

      const knownFields = ["label", ...optionalFields];
      const extraFields: string[] = [];

      const labelValues = new Set<string>();
      const duplicateLabels = new Set<string>();
      const emptyLabelRows: number[] = [];
      const translatedLabelIndex = headers.indexOf("translatedLabel");
      const translatedLabelToItemsMap = new Map<
        string,
        Array<{ plu: string; lineNumber: number }>
      >();

      optionalFields.forEach((field) => {
        if (headers.includes(field)) {
          foundOptional.push(field);
        } else {
          missingOptional.push(field);
        }
      });

      headers.forEach((header) => {
        if (!knownFields.includes(header)) {
          extraFields.push(header);
        }
      });

      setValidationResults({
        isCsv: true,
        hasLabel,
        foundOptional,
        missingOptional,
        emptyLabelRows: [], // Will be updated after row loop
        duplicateLabels: [], // Will be updated after row loop
        duplicateTranslatedLabels: [], // Will be updated after row loop
        extraFields,
      });

      // Data validation
      const priceByIndex = headers.indexOf("priceBy");
      const outOfScopeIndex = headers.indexOf("outOfScope");

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const row = line.split(",").map((v) => v.trim());

        // Detect empty/duplicate labels
        if (labelIndex !== -1) {
          const lbl = row[labelIndex];
          if (!lbl) {
            emptyLabelRows.push(i + 1);
          } else if (labelValues.has(lbl)) {
            duplicateLabels.add(lbl);
          } else {
            labelValues.add(lbl);
          }
        }

        // For group uploads, track translatedLabel -> PLU mappings to detect 1:1 violations
        if (translatedLabelIndex !== -1 && labelIndex !== -1) {
          const translatedLabel = row[translatedLabelIndex];
          const plu = row[labelIndex];
          if (translatedLabel && plu && translatedLabel !== plu) {
            if (!translatedLabelToItemsMap.has(translatedLabel)) {
              translatedLabelToItemsMap.set(translatedLabel, []);
            }
            translatedLabelToItemsMap
              .get(translatedLabel)!
              .push({ plu, lineNumber: i + 1 });
          }
        }

        // Validate priceBy: empty, "weight", or "quantity"
        if (priceByIndex !== -1 && row[priceByIndex]) {
          const val = row[priceByIndex];
          if (val !== "weight" && val !== "quantity") {
            errors.push(
              t(
                'Row {{row}}: "priceBy" must be "weight", "quantity", or empty (found "{{val}}")',
                { row: i + 1, val },
              ),
            );
          }
        }

        // Validate outOfScope: "true" or "false"
        if (outOfScopeIndex !== -1 && row[outOfScopeIndex]) {
          const val = row[outOfScopeIndex];
          if (val !== "true" && val !== "false") {
            errors.push(
              t(
                'Row {{row}}: "outOfScope" must be "true" or "false" (found "{{val}}")',
                { row: i + 1, val },
              ),
            );
          }
        }
      }

      // Find all translatedLabels that are assigned to multiple catalog items (1:1 violation)
      const duplicateTranslatedLabels: Array<{
        label: string;
        items: Array<{ plu: string; lineNumber: number }>;
      }> = [];
      for (const [label, items] of translatedLabelToItemsMap.entries()) {
        if (items.length > 1) {
          duplicateTranslatedLabels.push({ label, items });
        }
      }

      setValidationResults((prev) => ({
        ...prev,
        emptyLabelRows,
        duplicateLabels: Array.from(duplicateLabels),
        duplicateTranslatedLabels,
      }));

      if (errors.length > 0) {
        setValidationErrors(errors);
        setValidationState("invalid");
        return true;
      }

      setValidationState("valid");
      return true;
    } catch (error) {
      console.error("Error reading file:", error);
      setValidationErrors([t("Error reading file.")]);
      setValidationState("invalid");
      return false;
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent =
      uploadMode === "group"
        ? "label,name,englishName,price,family,priceBy,outOfScope,translatedLabel"
        : "label,name,englishName,price,family,priceBy,outOfScope";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "catalog_template.csv";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const Page1 = useMemo(
    () => (
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Update your product catalog by uploading a CSV file.
            <br /> Choose between updating the master catalog, store-specific
            pricing, or region-specific product codes.
          </p>
        </div>

        <FormField
          control={form.control}
          name="uploadMode"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="grid gap-4"
                >
                  {[
                    {
                      value: "master",
                      label: "Base Catalog Update",
                      desc: "Update base product information and default pricing for devices without a site.",
                      disabled: false,
                    },
                    {
                      value: "site",
                      label: "Site-Specific Catalog",
                      desc: "Upload catalog for a specific site. This will create or update site-specific catalog items.",
                      disabled: false,
                    },
                    {
                      value: "group",
                      label: "Site Group Catalog",
                      desc: "Upload catalog for all sites in a group. This will create or update catalog items for all sites in the selected group.",
                      disabled: false,
                    },
                  ].map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem
                        value={option.value}
                        id={option.value}
                        className="peer sr-only"
                        disabled={option.disabled}
                      />
                      <Label
                        htmlFor={
                          (option.value === "site" && uploadMode === "site") ||
                          (option.value === "group" && uploadMode === "group")
                            ? undefined
                            : option.value
                        }
                        className={cn(
                          "flex flex-col gap-3 rounded-md border-2 border-muted bg-popover p-4 peer-data-[state=checked]:border-azure peer-data-[state=checked]:bg-azure/10",
                          option.disabled
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-accent hover:text-accent-foreground cursor-pointer",
                        )}
                      >
                        <div
                          className={cn(
                            "flex flex-col gap-1",
                            (option.value === "site" &&
                              uploadMode === "site") ||
                              (option.value === "group" &&
                                uploadMode === "group")
                              ? "pointer-events-none"
                              : "",
                          )}
                        >
                          <span className="font-semibold">
                            {t(option.label)}
                          </span>
                          <span className="text-sm font-normal text-muted-foreground">
                            {t(option.desc)}
                          </span>
                        </div>

                        {/* Site selection inside site mode block */}
                        {option.value === "site" && uploadMode === "site" && (
                          <div
                            className="mt-2"
                            onClick={(e) => {
                              // Only stop propagation if not clicking on interactive elements
                              const target = e.target as HTMLElement;
                              if (
                                !target.closest('[role="button"]') &&
                                !target.closest("button")
                              ) {
                                e.stopPropagation();
                              }
                            }}
                          >
                            <FormSelectInputItem
                              form={form}
                              name="siteId"
                              label={t("selectSites")}
                              isMulti={false}
                              options={siteOptions}
                              mapping={siteMapping as any}
                              displaySelected={false}
                              container={{
                                className: "space-y-2",
                                sideBySide: false,
                                clear: null,
                              }}
                              placeholder={t("Select a site...")}
                            />
                          </div>
                        )}

                        {/* Group selection inside group mode block */}
                        {option.value === "group" && uploadMode === "group" && (
                          <div
                            className="mt-2"
                            onClick={(e) => {
                              // Only stop propagation if not clicking on interactive elements
                              const target = e.target as HTMLElement;
                              if (
                                !target.closest('[role="button"]') &&
                                !target.closest("button")
                              ) {
                                e.stopPropagation();
                              }
                            }}
                          >
                            <FormSelectInputItem
                              form={form}
                              name="groupId"
                              label={t("selectSiteGroups")}
                              isMulti={false}
                              options={groupOptions}
                              mapping={groupMapping as any}
                              displaySelected={false}
                              container={{
                                className: "space-y-2",
                                sideBySide: false,
                                clear: null,
                              }}
                              placeholder={t("Select a site group...")}
                            />
                          </div>
                        )}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    ),
    [form, t, uploadMode, siteOptions, groupOptions],
  );

  const renderTargetSummary = useCallback(
    (
      ids: number[],
      mapping: (id: number) => { label?: string } | undefined,
      singularKey: string,
      pluralKey: string,
    ) => {
      if (ids.length === 0) return null;
      const body =
        ids.length === 1
          ? t(singularKey, { name: mapping(ids[0])?.label ?? ids[0] })
          : t(pluralKey, {
              count: ids.length,
              names:
                ids
                  .slice(0, 3)
                  .map((id) => mapping(id)?.label ?? id)
                  .join(", ") +
                (ids.length > 3
                  ? t(" +{{count}} more", { count: ids.length - 3 })
                  : ""),
            });
      return (
        <div className="rounded-md bg-azure/10 p-2.5 border border-azure/20">
          <p className="text-sm font-medium text-azure">{body}</p>
        </div>
      );
    },
    [t],
  );

  const Page2 = useMemo(
    () => (
      <div className="space-y-3">
        <div className="text-left space-y-3">
          <h3 className="text-lg font-medium">
            {t("How to prepare your file")}
          </h3>

          {/* Show selected site(s)/group(s) info */}
          {renderTargetSummary(
            targetSiteIds,
            siteMapping,
            "Uploading to site: {{name}}",
            "Uploading to {{count}} sites: {{names}}",
          )}
          {renderTargetSummary(
            targetGroupIds,
            groupMapping,
            "Uploading to site group: {{name}}",
            "Uploading to {{count}} site groups: {{names}}",
          )}

          {/* Important Notes */}
          <div>
            <h4 className="font-medium mb-1.5 text-sm">
              {t("Important Notes:")}
            </h4>
            <ul className="list-disc pl-5 space-y-0.5 text-sm text-muted-foreground">
              <li>
                {t(
                  "The Label field is used to identify and update existing products in your catalog",
                )}
              </li>
              <li>
                {t(
                  "Empty cells will not overwrite existing data for that field",
                )}
              </li>
              <li>{t("File format must be CSV")}</li>
            </ul>
          </div>

          {/* Required Fields */}
          <div>
            <h4 className="font-medium mb-1.5 text-sm">
              {t("Required Fields:")}
            </h4>
            <ul className="list-disc pl-5 space-y-0.5 text-sm text-muted-foreground">
              <li>{t("Label - Product label/code")}</li>
            </ul>
          </div>

          {/* Optional Fields */}
          <div>
            <h4 className="font-medium mb-1.5 text-sm">
              {t("Optional Fields:")}
            </h4>
            <ul className="list-disc pl-5 space-y-0.5 text-sm text-muted-foreground">
              <li>{t("Name - Localized name")}</li>
              <li>{t("English Name - Localized name")}</li>
              <li>{t("Family - Product category")}</li>
              <li>{t("priceBy") + " - " + "weight/quantity"}</li>
              <li>{t("Price")}</li>
              {uploadMode === "group" && (
                <li>{t("Translated Label - Group-specific label override")}</li>
              )}
            </ul>
          </div>

          {/* Download Template */}
          <div className="pt-1 space-y-2">
            <p className="text-sm text-muted-foreground text-center">
              {t("Need formatting help? Download our template CSV file")}
            </p>
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                className="gap-2 focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                onClick={handleDownloadTemplate}
                tabIndex={-1}
              >
                <Download className="h-4 w-4" />
                {t("Download Template")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    ),
    [
      t,
      uploadMode,
      targetSiteIds,
      targetGroupIds,
      siteMapping,
      groupMapping,
      renderTargetSummary,
    ],
  );

  const Page3 = useMemo(
    () => (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t("Select File")}</h3>

        <FormField
          control={form.control}
          name="file"
          render={() => (
            <FormItem>
              <FormControl>
                <div
                  {...getRootProps()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-10 text-center cursor-pointer hover:bg-accent/50 transition-colors flex flex-col items-center justify-center gap-4 min-h-[200px]",
                    isDragActive
                      ? "border-azure bg-azure/5"
                      : "border-muted-foreground/25",
                  )}
                >
                  <input {...getInputProps()} />
                  <div className="rounded-full bg-muted p-4">
                    <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    {file ? (
                      <>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          className="mt-2"
                        >
                          {t("changeFile")}
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium">
                          {t("Drag and drop your file here")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("or click to browse")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {t("Only CSV files are allowed")}
                        </p>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          className="mt-2"
                        >
                          {t("Browse")}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Upload Mode Radio Button - Only for site-group uploads */}
        {targetGroupIds.length > 0 && (
          <FormField
            control={form.control}
            name="labelUploadMode"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("Upload Mode")}:
                  </Label>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex flex-col space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="full" id="full" />
                        <Label
                          htmlFor="full"
                          className="font-normal cursor-pointer"
                        >
                          <span className="font-semibold">{t("Full")}</span>:{" "}
                          {t(
                            "replace all label translations in the group with those in the file",
                          )}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="partial" id="partial" />
                        <Label
                          htmlFor="partial"
                          className="font-normal cursor-pointer"
                        >
                          <span className="font-semibold">{t("Partial")}</span>:{" "}
                          {t(
                            "add the label translations that are in the file to those already in the site-group (override if already exists)",
                          )}
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    ),
    [file, getRootProps, getInputProps, isDragActive, t, targetGroupIds],
  );

  const Page4 = useMemo(
    () => (
      <div className="space-y-4">
        <div className="space-y-2">
          <h1>{t("chooseWhenYouWantToUpload")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("Select whether to upload immediately or schedule for later")}
          </p>
        </div>

        <div
          className={cn("border p-2 rounded-md mb-2", {
            "border-azure bg-azure/10": runImmediately,
          })}
        >
          <FormField
            control={form.control}
            name="runImmediately"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RunImmediatelyInput
                    {...field}
                    description={t("uploadCatalogRightAway")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div
          className={cn("border p-2 rounded-md mb-2", {
            "border-azure bg-azure/10": !runImmediately,
          })}
        >
          <FormField
            control={form.control}
            name="runImmediately"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ScheduleSpecificInput {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-2 ml-5 mt-4">
            <FormDatePickerItem
              {...timeSettings.as(timezone)}
              name="schedule"
              form={form}
              label={t("date")}
              button={{ disabled: runImmediately }}
              container={{ className: runImmediately ? "opacity-50" : "" }}
            />
            <FormField
              key={`timezone-${currentStep}`}
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem className={runImmediately ? "opacity-50" : ""}>
                  <FormLabel>{t("timezone")}</FormLabel>
                  <FormControl>
                    <TzPicker
                      className="w-full"
                      value={field.value as TimeSettings["timezone"]}
                      onChange={(tz) => field.onChange(tz)}
                      isDisabled={runImmediately}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <FormField
          control={form.control}
          name="jobName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Job name")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    isJobNameCustomized.current = true;
                    field.onChange(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormCheckbox
          form={form}
          name="immediateApply"
          label={t("Apply immediately to devices")}
          container={{
            className: "flex-row-reverse justify-end gap-2",
            sideBySide: true,
            clear: null,
          }}
        />
      </div>
    ),
    [
      currentStep,
      form,
      isJobNameCustomized,
      jobName,
      runImmediately,
      t,
      timeSettings,
      timezone,
    ],
  );

  const Page5 = useMemo(
    () => (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t("Validation Results")}</h3>

        {validationState === "validating" && (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-azure" />
            <p className="text-sm text-muted-foreground">
              {t("Validating file...")}
            </p>
          </div>
        )}

        {(validationState === "valid" || validationState === "invalid") && (
          <>
            {validationErrors.length > 0 && (
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t("Validation Failed")}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <ul className="list-disc pl-5 space-y-1">
                        {validationErrors.slice(0, 4).map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                      {validationErrors.length > 4 && (
                        <p className="mt-2 font-medium italic">
                          {t("And {{count}} more errors...", {
                            count: validationErrors.length - 4,
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {validationResults.isCsv &&
              validationResults.duplicateLabels.length === 0 &&
              validationResults.duplicateTranslatedLabels.length === 0 && (
                <div className="rounded-md bg-green-50 p-4 border border-green-200">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle2
                        className="h-5 w-5 text-green-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        {t("Validation Successful")}
                      </h3>
                      <div className="mt-2 text-sm text-green-700">
                        <ul className="list-disc pl-5 space-y-1">
                          <li>{t("File is CSV")}</li>
                          {validationResults.hasLabel && (
                            <li>{t('"label" field exists')}</li>
                          )}
                          {validationResults.foundOptional.map((field) => (
                            <li key={field}>
                              {t('"{{field}}" field exists', { field })}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {(validationResults.duplicateLabels.length > 0 ||
              validationResults.duplicateTranslatedLabels.length > 0) && (
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t("Errors")}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      {validationResults.duplicateLabels.length > 0 && (
                        <div className="mb-4 text-xs font-normal">
                          <p className="mb-2 font-medium">
                            {t("Duplicate labels found:")}
                          </p>
                          <ul className="list-disc pl-5 space-y-1">
                            {validationResults.duplicateLabels
                              .slice(0, 5)
                              .map((lbl) => (
                                <li key={lbl}>{lbl}</li>
                              ))}
                            {validationResults.duplicateLabels.length > 5 && (
                              <li className="italic">
                                {t("...and {{count}} more", {
                                  count:
                                    validationResults.duplicateLabels.length -
                                    5,
                                })}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {validationResults.duplicateTranslatedLabels.length >
                        0 && (
                        <div className="text-xs font-normal">
                          <p className="mb-2 font-medium">
                            {t(
                              "Duplicate translated labels found (1:1 violation):",
                            )}
                          </p>
                          <ul className="list-disc pl-5 space-y-1">
                            {validationResults.duplicateTranslatedLabels
                              .slice(0, 5)
                              .map(({ label, items }) => (
                                <li key={label}>
                                  <span className="font-semibold">{label}</span>
                                  {t(" is assigned to: ")}
                                  {items.map((item, idx) => (
                                    <span key={idx}>
                                      {item.plu} (line {item.lineNumber})
                                      {idx < items.length - 1 ? ", " : ""}
                                    </span>
                                  ))}
                                </li>
                              ))}
                            {validationResults.duplicateTranslatedLabels
                              .length > 5 && (
                              <li className="italic">
                                {t("...and {{count}} more", {
                                  count:
                                    validationResults.duplicateTranslatedLabels
                                      .length - 5,
                                })}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(validationResults.missingOptional.length > 0 ||
              validationResults.emptyLabelRows.length > 0) && (
              <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle
                      className="h-5 w-5 text-amber-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      {t("Warnings")}
                    </h3>
                    <div className="mt-2 text-sm text-amber-700">
                      {validationResults.extraFields.length > 0 && (
                        <div className="mb-4">
                          <p className="mb-2 font-medium text-red-600">
                            {t("Unknown Fields Found:")}
                          </p>
                          <ul className="list-disc pl-5 space-y-1 text-red-700">
                            {validationResults.extraFields.map((field) => (
                              <li key={field} className="font-mono text-xs">
                                {field}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-1 text-xs text-red-600/80">
                            {t("These fields will be ignored.")}
                          </p>
                        </div>
                      )}

                      {validationResults.missingOptional.length > 0 && (
                        <div className="mb-4 text-xs font-normal">
                          <p className="mb-2 font-medium">
                            {t("Missing optional fields:")}
                          </p>
                          <ul className="list-disc pl-5 space-y-1">
                            {validationResults.missingOptional.map((field) => (
                              <li key={field}>
                                {t('"{{field}}" field is missing', { field })}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {validationResults.emptyLabelRows.length > 0 && (
                        <div className="text-xs font-normal">
                          <p className="mb-2 font-medium">
                            {t("Rows with empty label:")}
                          </p>
                          <ul className="list-disc pl-5 space-y-1">
                            {validationResults.emptyLabelRows
                              .slice(0, 5)
                              .map((row) => (
                                <li key={row}>{t("Row {{row}}", { row })}</li>
                              ))}
                            {validationResults.emptyLabelRows.length > 5 && (
                              <li className="italic">
                                {t("...and {{count}} more", {
                                  count:
                                    validationResults.emptyLabelRows.length - 5,
                                })}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    ),
    [validationState, validationErrors, validationResults, t],
  );

  const Page6 = useMemo(() => {
    // Show the item number being processed (ceiling of current)
    const currentItem = uploadProgress ? Math.ceil(uploadProgress.current) : 0;

    return (
      <div className="space-y-6 text-center flex flex-col items-center justify-center min-h-[300px]">
        {uploadError ? (
          <>
            <div className="rounded-full bg-red-100 p-4 w-fit">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
            <div className="space-y-2 max-w-2xl">
              <h3 className="text-xl font-semibold text-red-800">
                {t("Upload Failed")}
              </h3>
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <p className="text-sm text-red-700 whitespace-pre-wrap text-left">
                  {uploadError}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="azure"
              onClick={onClose}
              className="mt-4"
            >
              {t("Close")}
            </Button>
          </>
        ) : uploadProgress ? (
          <>
            <div className="rounded-full bg-azure/10 p-4 w-fit">
              <Loader2 className="h-12 w-12 text-azure animate-spin" />
            </div>
            <div className="space-y-4 w-full max-w-md">
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-azure">
                  {t("Uploading...")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("Processing group {{current}} of {{total}}", {
                    current: currentItem,
                    total: uploadProgress.total,
                  })}
                </p>
              </div>
              <Progress
                value={animatedProgress}
                variant="running"
                className="h-2"
              />
            </div>
          </>
        ) : (
          <>
            <div className="rounded-full bg-green-100 p-4 w-fit">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-green-800">
                {t("Upload Successful!")}
              </h3>
              {targetSiteIds.length > 1 && (
                <p className="text-sm text-muted-foreground">
                  {t("Catalog upload job created for {{count}} sites", {
                    count: targetSiteIds.length,
                  })}
                </p>
              )}
              {targetGroupIds.length > 1 && (
                <p className="text-sm text-muted-foreground">
                  {t("Catalog upload job created for {{count}} site groups", {
                    count: targetGroupIds.length,
                  })}
                </p>
              )}
              {/* For a multi-target upload, the "Catalog upload job created for N sites/groups" message above
                  already describes the single job that was created — showing "N upload jobs scheduled" here too
                  would read as contradictory, since it's the same one job, not a job per site/group. */}
              {targetSiteIds.length <= 1 &&
                targetGroupIds.length <= 1 &&
                (uploadResults?.scheduledJobs ?? 0) > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t("{{count}} upload jobs scheduled", {
                      count: uploadResults?.scheduledJobs,
                    })}
                  </p>
                )}
              {targetSiteIds.length <= 1 &&
                targetGroupIds.length <= 1 &&
                (uploadResults?.immediateUploads ?? 0) > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t("{{count}} uploads executed", {
                      count: uploadResults?.immediateUploads,
                    })}
                  </p>
                )}
              {uploadResults?.updatedLabels !== undefined && (
                <p className="text-sm text-muted-foreground">
                  {t("{{count}} labels updated", {
                    count: uploadResults.updatedLabels,
                  })}
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="azure"
              onClick={onClose}
              className="mt-4"
            >
              {t("Done")}
            </Button>
          </>
        )}
      </div>
    );
  }, [
    uploadResults,
    uploadError,
    uploadProgress,
    animatedProgress,
    targetSiteIds,
    targetGroupIds,
    t,
    onClose,
  ]);

  const pages: DialogPage[] = useMemo(
    () => [
      {
        title: t("Mode"),
        content: Page1,
        fields: ["uploadMode", "siteId", "groupId"],
        // targetSiteIds/targetGroupIds already account for both a bulk pre-fill and a manual
        // Page1 pick — checking them here (not the raw form fields) is what keeps "Next" usable
        // after Back-navigating into a bulk upload that was opened with a multi-site selection.
        disableNext:
          (uploadMode === "site" && targetSiteIds.length === 0) ||
          (uploadMode === "group" && targetGroupIds.length === 0),
        blocker: () => {
          const mode = form.getValues("uploadMode");
          if (mode === "site" && targetSiteIds.length === 0) return false;
          if (mode === "group" && targetGroupIds.length === 0) return false;
          return true;
        },
      },
      {
        title: t("Preparation"),
        content: Page2,
      },
      {
        title: t("Upload"),
        content: Page3,
        disableNext: !file,
        fields: ["file", "labelUploadMode"],
        blocker: async () => {
          const isValid = await form.trigger("file");
          if (!isValid || !file) return false;
          return true;
        },
      },
      {
        title: t("Scheduler"),
        content: Page4,
        fields: runImmediately ? [] : ["schedule", "timezone"],
        blocker: async () => {
          const isScheduleValid = await form.trigger(
            runImmediately ? [] : ["schedule", "timezone"],
          );
          if (!isScheduleValid || !file) return false;
          // Run validation when moving from Scheduler to Validation
          return await validateFile(file);
        },
      },
      {
        title: t("Validation"),
        content: Page5,
        disableNext: (() => {
          const isGroupUpload = targetGroupIds.length > 0;
          const hasDuplicateLabels =
            validationResults.duplicateLabels.length > 0;
          const hasDuplicateTranslatedLabels =
            validationResults.duplicateTranslatedLabels.length > 0;

          // Disable if validation failed
          if (validationState !== "valid") return true;

          // Disable if there are duplicate labels (for any upload type)
          if (hasDuplicateLabels) return true;

          // Disable if there are duplicate translated labels (for group uploads)
          if (isGroupUpload && hasDuplicateTranslatedLabels) return true;

          return false;
        })(),
        // Only allow submitting if validation is valid AND no duplicate labels AND no duplicate translated labels for group uploads
        blocker: () => {
          const isGroupUpload = targetGroupIds.length > 0;
          const hasDuplicateLabels =
            validationResults.duplicateLabels.length > 0;
          const hasDuplicateTranslatedLabels =
            validationResults.duplicateTranslatedLabels.length > 0;

          // Block if validation failed
          if (validationState !== "valid") return false;

          // Block if there are duplicate labels (for any upload type)
          if (hasDuplicateLabels) return false;

          // Block if there are duplicate translated labels (for group uploads)
          if (isGroupUpload && hasDuplicateTranslatedLabels) return false;

          return true;
        },
        hideNext: validationState === "invalid",
      },
      {
        title: t("Results"),
        content: Page6,
        hideNext: true, // Hide the "Finish" button, we'll use a custom "Done" button
        footer: !uploadProgress ? (
          <Button type="button" variant="azure" onClick={onClose}>
            {t("Done")}
          </Button>
        ) : null,
      },
    ],
    [
      Page1,
      Page2,
      Page3,
      Page4,
      Page5,
      Page6,
      t,
      file,
      form,
      validateFile,
      validationState,
      uploadMode,
      runImmediately,
      siteId,
      groupId,
      targetSiteIds,
      targetGroupIds,
      uploadProgress,
      onClose,
    ],
  );

  const handleNext = useCallback(async () => {
    const fields = pages[currentStep].fields;
    const isStepValid = fields
      ? await form.trigger(
          fields as (keyof z.infer<typeof uploadCatalogSchema>)[],
          { shouldFocus: true },
        )
      : true;

    if (isStepValid) {
      // If we're on Validation (index 4) and moving to Results (index 5)
      // trigger the upload
      if (currentStep === 4 && file) {
        // Clear any previous errors
        setUploadError(null);
        setUploadResults(null);

        // Advance to results page immediately to show progress
        setCurrentStep(5);

        // Then trigger the upload
        const formValues = form.getValues();
        uploadMutation.mutate({
          file,
          siteIds: targetSiteIds.length > 0 ? targetSiteIds : undefined,
          groupIds: targetGroupIds.length > 0 ? targetGroupIds : undefined,
          labelUploadMode: formValues.labelUploadMode,
          schedule:
            !formValues.runImmediately && formValues.schedule
              ? {
                  name: formValues.jobName || computedJobName,
                  startDate: new Date(formValues.schedule),
                  timezone: formValues.timezone,
                  runOnce: true,
                }
              : undefined,
          immediateApply: formValues.immediateApply,
          name: formValues.jobName || undefined,
        });
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    }
  }, [
    currentStep,
    pages,
    form,
    file,
    uploadMutation,
    targetSiteIds,
    targetGroupIds,
    computedJobName,
  ]);

  const handleBack = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  return (
    <div className="w-[600px] min-h-[650px] flex flex-col">
      <Form {...form}>
        <form
          onSubmit={(e) => e.preventDefault()}
          className="flex-1 flex flex-col h-full"
        >
          <MultiStepDialogContent
            className="flex-1"
            title={t("Upload Product Catalog")}
            pages={pages}
            currentStep={currentStep}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={() => {}}
            finishButtonText={t("Upload")}
          />
        </form>
      </Form>
    </div>
  );
};
